import type { AssignmentCoverageRow } from '@/api/types/compliance-dashboard.types';

// Slice 2 (BI): completed / in-progress / not-started controls per assignee (the effective
// L1/L2/L3 workload), with the synthetic Unassigned queue surfaced first. The bar shows the
// completed proportion; the breakdown reads completed / in-progress / not-started.
interface AssignmentCoveragePanelProps {
  data: AssignmentCoverageRow[];
  isLoading: boolean;
}

export function AssignmentCoveragePanel({ data, isLoading }: AssignmentCoveragePanelProps) {
  // Unassigned queue first, then assignees by descending total.
  const rows = [...data].sort((a, b) => {
    if (a.is_unassigned !== b.is_unassigned) return a.is_unassigned ? -1 : 1;
    return b.total - a.total;
  });

  return (
    <div
      className="flex min-w-0 flex-col gap-[11px] rounded-xl border border-border-subtle bg-bg-surface p-[14px_16px_13px]"
      data-testid="assignment-coverage"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-text-primary">Assignment coverage</span>
        <span className="ml-auto text-[10.5px] text-[#7A8CA3]">Per assignee</span>
      </div>
      {isLoading || rows.length === 0 ? (
        <p className="text-[11.5px] text-text-secondary">
          {isLoading ? 'Loading…' : 'No controls are assigned yet.'}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((r) => {
            const pct = r.total > 0 ? (r.completed / r.total) * 100 : 0;
            return (
              <div className="flex items-center gap-[11px]" key={`${r.name}-${r.note ?? ''}`} data-testid="assignee-row">
                <span className="flex min-w-0 flex-[0_0_120px] flex-col gap-[1px]">
                  <span
                    className="truncate text-[12px] font-semibold"
                    style={{ color: r.is_unassigned ? '#F0C674' : '#F8FAFC' }}
                  >
                    {r.name}
                  </span>
                  {r.note && <span className="truncate text-[10px] text-[#6E829B]">{r.note}</span>}
                </span>
                <span className="flex h-[9px] min-w-0 flex-[1_1_auto] overflow-hidden rounded-[3px] bg-[#0D1626]">
                  <span className="block h-full" style={{ width: `${pct}%`, background: '#4A9E78' }} />
                </span>
                <span className="flex-[0_0_96px] whitespace-nowrap text-right text-[11px] tabular-nums text-text-secondary">
                  {r.completed} / {r.in_progress} / {r.not_started}
                </span>
                <span className="flex-[0_0_34px] text-right text-[11.5px] font-semibold tabular-nums text-text-muted">
                  {r.total}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <span className="mt-auto border-t border-[#1B2C43] pt-[9px] text-[11.5px] leading-[1.55] text-text-secondary">
        Completed / in-progress / not-started controls by assignee. Unassigned work appears first.
      </span>
    </div>
  );
}
